import { useCallback, useEffect, useRef, useState } from 'react';
import type { Socket } from 'socket.io-client';
import api from '../services/api';
import { retainAppSocket, getAppSocket } from '../services/socketService';
import { useSettings } from '../contexts/SettingsContext';
import { APP_BRAND } from '../config/branding';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

/** Subscribe to Web Push and show mail alerts when the tab is in the background. */
export function useWebPush(enabled: boolean): {
  supported: boolean;
  subscribed: boolean;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
} {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const { appConfig } = useSettings();
  const appName = appConfig.system.appName?.trim() || APP_BRAND.name;

  const registerServiceWorker = useCallback(async (): Promise<ServiceWorkerRegistration | null> => {
    if (!('serviceWorker' in navigator)) return null;
    try {
      const reg = await navigator.serviceWorker.register(
        `${process.env.PUBLIC_URL || ''}/sw-push.js`,
        { scope: '/' },
      );
      registrationRef.current = reg;
      return reg;
    } catch {
      return null;
    }
  }, []);

  const subscribe = useCallback(async () => {
    if (!supported) return;
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;

    const { data } = await api.get<{ publicKey: string | null; enabled: boolean }>('/push/vapid-public-key');
    if (!data.enabled || !data.publicKey) return;

    const reg = registrationRef.current ?? (await registerServiceWorker());
    if (!reg) return;

    const existing = await reg.pushManager.getSubscription();
    const sub =
      existing ??
      (await reg.pushManager.subscribe({
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        userVisibleOnly: true,
      }));

    const json = sub.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return;

    await api.post('/push/subscribe', {
      endpoint: json.endpoint,
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
    });
    setSubscribed(true);
  }, [registerServiceWorker, supported]);

  const unsubscribe = useCallback(async () => {
    const reg = registrationRef.current;
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) {
      setSubscribed(false);
      return;
    }
    const json = sub.toJSON();
    if (json.endpoint) {
      await api.delete('/push/subscribe', { data: { endpoint: json.endpoint } }).catch(() => undefined);
    }
    await sub.unsubscribe();
    setSubscribed(false);
  }, []);

  useEffect(() => {
    const ok =
      typeof window !== 'undefined' &&
      'Notification' in window &&
      'serviceWorker' in navigator &&
      'PushManager' in window;
    setSupported(ok);
    if (!ok || !enabled) return;

    void registerServiceWorker().then(async (reg) => {
      if (!reg) return;
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    });
  }, [enabled, registerServiceWorker]);

  useEffect(() => {
    if (!enabled || !supported) return;

    const release = retainAppSocket();
    if (!release) return;

    const socket = getAppSocket();
    if (!socket) {
      release();
      return;
    }
    socketRef.current = socket;

    const showBackgroundMailAlert = () => {
      if (!document.hidden) return;
      if (Notification.permission !== 'granted') return;
      new Notification(appName, {
        body: 'New mail arrived',
        icon: '/favicon.svg',
        tag: 'new-mail',
      });
    };

    socket.on('emails:invalidate', showBackgroundMailAlert);

    return () => {
      socket.off('emails:invalidate', showBackgroundMailAlert);
      release();
      socketRef.current = null;
    };
  }, [enabled, supported, appName]);

  return { supported, subscribed, subscribe, unsubscribe };
}
